import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { ApiError, session, type CurrentUser, type LoginPayload } from '@/core';
// routes.ts 只导出路由表与常量、不回引 store，无循环依赖
import { HOME_PATH } from '@/router/routes';
import * as authApi from '@/api/auth';

/** 423 应答若带解锁时刻则以其为准，缺失时按 §6.0-④ 冻结的 15min 名义窗口兜底 */
function serverUnlockAt(err: ApiError): string {
  const payload = err.payload;
  if (payload && typeof payload === 'object') {
    const raw = (payload as Record<string, unknown>).unlockAt ?? (payload as Record<string, unknown>).lockedUntil;
    if (typeof raw === 'string' && raw) return raw;
    if (typeof raw === 'number' && raw > 0) return new Date(raw).toISOString();
  }
  return new Date(Date.now() + session.ACCOUNT_LOCK_MINUTES * 60 * 1000).toISOString();
}

/** auth store — 登录态 / 令牌 / 会话 30min 超时 / 登录失败锁定倒计时 */
export const useAuthStore = defineStore('auth', () => {
  const user = ref<CurrentUser | null>(session.getSessionUser());
  const expiresAt = ref<number>(session.sessionExpiresAt());
  const loading = ref(false);
  const lockedUntil = ref<number>(session.lockedUntil());
  const failCount = ref(0);

  const isAuthenticated = computed(() => !!user.value && !!session.getAccessToken());
  const permissions = computed(() => user.value?.permissions ?? []);

  const lockRemainingSeconds = computed(() => {
    const left = lockedUntil.value - Date.now();
    return left > 0 ? Math.ceil(left / 1000) : 0;
  });

  async function login(payload: LoginPayload): Promise<CurrentUser> {
    loading.value = true;
    try {
      const result = await authApi.login(payload);
      session.saveSession(result);
      session.resetLoginFailure();
      user.value = result.user;
      // 登录快照只由 JWT claims 复原（`userFromJwtClaims`），里面没有权限码。
      // 同一份 document 内二次登录（超时登出后重登、切号自测）若沿用上一个账号的 'ok' 状态，
      // §6.2-7 的 profile 重建会被跳过，新账号就带着空权限进路由守卫——机构管理员会被判成
      // 「无 menu:order」直接踢到 /forbidden，角色分流也永远不生效。置回 pending 让重建必然发生。
      snapshotState.value = 'pending';
      expiresAt.value = session.sessionExpiresAt();
      failCount.value = 0;
      lockedUntil.value = 0;
      return result.user;
    } catch (err) {
      // §6.0-④：423 由服务端权威判定，本地必须同步锁定窗口，否则刷新即解锁、继续重试只会延长
      const locked = err instanceof ApiError && err.isAccountLocked;
      const unlockAt = locked ? serverUnlockAt(err) : undefined;
      const info = session.recordLoginFailure(unlockAt);
      failCount.value = info.count;
      lockedUntil.value = info.lockedUntil;
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function logout(callApi = true): Promise<void> {
    if (callApi) {
      try {
        await authApi.logout();
      } catch {
        /* 登出失败不阻塞本地清理 */
      }
    }
    session.clearSession();
    user.value = null;
    expiresAt.value = 0;
    snapshotState.value = 'pending';
  }

  function forceLogout(): void {
    session.clearSession();
    user.value = null;
    expiresAt.value = 0;
    snapshotState.value = 'pending';
    // 兜底回跳用本端落地页：录单端没有 /dashboard，沿用管理端路径会把用户送到一条不存在的路由
    const redirect = encodeURIComponent(window.location.hash.replace(/^#/, '') || HOME_PATH);
    window.location.hash = `#/login?redirect=${redirect}&reason=timeout`;
  }

  function restore(): CurrentUser | null {
    const stored = session.getSessionUser();
    user.value = stored;
    expiresAt.value = session.sessionExpiresAt();
    return stored;
  }

  /** §6.2-7 快照同步状态：'pending' 未发起 / 'ok' 已按 profile 重建 / 'stale' 端点不可用（沿用 interim 快照） */
  const snapshotState = ref<'pending' | 'ok' | 'stale'>('pending');
  const snapshotError = ref('');

  /**
   * §3.5-② / §6.2-7：刷新页面后**必须**用 `GET /auth/profile` 重建权限态，
   * 不得沿用 localStorage 旧快照（`permVersion` 可能已变）。
   * 端点不可用时不静默假装已刷新：保留登录时服务端签发的 interim 快照并记原因，供界面提示「权限待确认」。
   */
  async function syncProfile(): Promise<void> {
    if (snapshotState.value === 'ok' || !session.getAccessToken()) return;
    try {
      const fresh = await authApi.fetchProfile();
      // t108：profile 面**不下发**首登位（后端只把它放在登录响应里）⇒ 重建权限态时必须**保留**登录时带下来的
      // forcePwdChange，否则刷新页面会把它抹成 undefined，一次 reload 就绕过强制改密拦截。
      const merged = { ...fresh, forcePwdChange: user.value?.forcePwdChange === true };
      user.value = merged;
      session.saveSessionUser(merged);
      expiresAt.value = session.sessionExpiresAt();
      snapshotState.value = 'ok';
      snapshotError.value = '';
      // permVersion 变化时无需手动失效缓存：permission store 直接读 user.permissions，已是新值
    } catch (err) {
      snapshotState.value = 'stale';
      snapshotError.value = err instanceof Error ? err.message : String(err);
    }
  }

  /** 供界面/排查判断当前权限态是否已由 §6.2-7 重建 */
  const snapshotFresh = computed(() => snapshotState.value === 'ok');

  /** t108：改密成功后本地清位（服务端已清两族成员表；快照同步置 false ⇒ 守卫不再拦）。 */
  function clearForcePwdChange(): void {
    if (!user.value) return;
    user.value = { ...user.value, forcePwdChange: false };
    session.saveSessionUser(user.value);
  }

  return {
    user,
    expiresAt,
    loading,
    clearForcePwdChange,
    failCount,
    lockedUntil,
    lockRemainingSeconds,
    isAuthenticated,
    permissions,
    login,
    logout,
    forceLogout,
    restore,
    syncProfile,
    snapshotState,
    snapshotError,
    snapshotFresh,
  };
});
